import React, { useState } from 'react';
import {
  Container, Typography, Grid, Box, Card, CardContent, Button,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Stepper, Step, StepLabel, Chip, Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { saveOrder } from '../services/db';
import { notifyInternal, notifyCustomer } from '../services/email';
import { orderConfirmationEmail } from '../services/emailTemplates';
import StripePaymentForm from '../components/StripePaymentForm';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const STEPS = ['Your Details', 'Payment'];

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useCart();
  const navigate = useNavigate();
  const [checkoutDialog, setCheckoutDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '', lastName: '', email: '', phone: '', address: '',
  });

  const calculateTotal = () =>
    cartItems.reduce((total, item) => total + item.price * item.quantity, 0);

  const hasRequestItems = cartItems.some(item => item.isRequest);

  const handleCheckout = () => {
    setCheckoutDialog(true);
    setActiveStep(0);
    setClientSecret('');
    setPaymentError('');
  };

  const handleCloseCheckout = () => {
    setCheckoutDialog(false);
    setActiveStep(0);
    setClientSecret('');
    setPaymentError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const infoComplete =
    customerInfo.firstName && customerInfo.lastName &&
    customerInfo.email && customerInfo.phone && customerInfo.address;

  const handleContinueToPayment = async () => {
    setPaymentError('');
    try {
      const amountCents = Math.round(calculateTotal() * 100);
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/create-payment-intent.php'
        : '/api/create-payment-intent';
      const { data } = await axios.post(apiUrl, { amount: amountCents });
      if (!data.success) throw new Error(data.error);
      setClientSecret(data.clientSecret);
      setActiveStep(1);
    } catch (err) {
      setPaymentError(err.response?.data?.error || err.message || 'Could not start payment. Try again.');
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    const newOrder = {
      id: `ORD-${Date.now().toString(36).toUpperCase()}`,
      date: new Date().toISOString().split('T')[0],
      customerName: `${customerInfo.firstName} ${customerInfo.lastName}`,
      email: customerInfo.email,
      phone: customerInfo.phone,
      shippingAddress: customerInfo.address,
      items: cartItems.map(item => ({ name: item.name, quantity: item.quantity, price: item.price })),
      total: calculateTotal(),
      status: 'Paid',
      paymentIntentId,
      createdAt: new Date().toISOString(),
    };

    try {
      const local = JSON.parse(localStorage.getItem('orders') || '[]');
      local.unshift(newOrder);
      localStorage.setItem('orders', JSON.stringify(local));
    } catch (_) {}

    try { await saveOrder(newOrder); } catch (_) {}

    const itemLines = cartItems.map(i => `  • ${i.name} ×${i.quantity} — $${(i.price * i.quantity).toFixed(2)}`).join('\n');
    try {
      await notifyInternal(
        `New PAID Order — ${newOrder.id}`,
        `Customer: ${customerInfo.firstName} ${customerInfo.lastName} <${customerInfo.email}>\nPhone: ${customerInfo.phone}\n\nItems:\n${itemLines}\n\nTotal: $${calculateTotal().toFixed(2)}\nShipping: ${customerInfo.address}\nStripe ID: ${paymentIntentId}`,
        customerInfo.email,
      );
      await notifyCustomer(
        customerInfo.email,
        `Order Confirmed — ${newOrder.id} | AkdmeTech`,
        orderConfirmationEmail({ firstName: customerInfo.firstName, orderId: newOrder.id, items: cartItems, total: calculateTotal(), address: customerInfo.address }),
      );
    } catch (_) {}

    clearCart();
    handleCloseCheckout();
    navigate('/thank-you');
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(255, 217, 61, 0.2) 100%)',
    backdropFilter: 'blur(10px)',
    borderRadius: '15px',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
  };

  return (
    <Box sx={{ bgcolor: 'background.default', py: 8 }}>
      <Container>
        <Typography variant="h2" align="center" gutterBottom className="gradient-text" sx={{ mb: 6 }}>
          Your Cart
        </Typography>
        <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 8, maxWidth: 800, mx: 'auto' }}>
          Review and manage your selected items
        </Typography>

        <Grid container spacing={4}>
          {/* Cart Items */}
          <Grid item xs={12} md={8}>
            {cartItems.length === 0 ? (
              <Card className="glass-effect" sx={{ ...cardStyle, p: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>Your cart is empty</Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/products')}
                  sx={{
                    mt: 2,
                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                    color: 'background.default',
                  }}
                >
                  Browse Products
                </Button>
              </Card>
            ) : (
              cartItems.map((item) => (
                <Card key={item.id} className="glass-effect" sx={{ ...cardStyle, mb: 3, transition: 'all 0.3s ease-in-out', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Grid container alignItems="center" spacing={3}>
                      <Grid item xs={12} sm={3}>
                        <Box component="img" src={item.image} alt={item.name} sx={{ width: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="h6" gutterBottom className="gradient-text">{item.name}</Typography>
                        {item.isRequest ? (
                          <Chip
                            icon={<HourglassTopIcon sx={{ fontSize: 14 }} />}
                            label="Awaiting Quote"
                            size="small"
                            sx={{ bgcolor: 'rgba(255,217,61,0.15)', color: '#ffd93d', border: '1px solid rgba(255,217,61,0.3)', fontWeight: 600 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">${item.price.toFixed(2)}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IconButton onClick={() => updateQuantity(item.id, -1)} size="small" sx={{ color: 'primary.main' }}>
                            <RemoveIcon />
                          </IconButton>
                          <Typography sx={{ mx: 2, minWidth: '30px', textAlign: 'center' }}>{item.quantity}</Typography>
                          <IconButton onClick={() => updateQuantity(item.id, 1)} size="small" sx={{ color: 'primary.main' }}>
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ mb: 1 }} className="gradient-text">
                          {item.isRequest ? 'TBD' : `$${(item.price * item.quantity).toFixed(2)}`}
                        </Typography>
                        <IconButton onClick={() => removeFromCart(item.id)} size="small" sx={{ color: 'error.main' }}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))
            )}
          </Grid>

          {/* Order Summary */}
          <Grid item xs={12} md={4}>
            <Card className="glass-effect" sx={{ ...cardStyle, position: 'sticky', top: '100px' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom className="gradient-text" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCartIcon /> Order Summary
                </Typography>
                <Box sx={{ mt: 4 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}><Typography variant="body1">Subtotal:</Typography></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body1" className="gradient-text">${calculateTotal().toFixed(2)}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="body1">Shipping:</Typography></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="body1" className="gradient-text">Free</Typography></Grid>
                    <Grid item xs={12}><Box sx={{ my: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }} /></Grid>
                    <Grid item xs={6}><Typography variant="h6">Total:</Typography></Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}><Typography variant="h6" className="gradient-text">${calculateTotal().toFixed(2)}</Typography></Grid>
                  </Grid>
                  {hasRequestItems ? (
                    <Box sx={{ mt: 3 }}>
                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
                      <Typography variant="subtitle2" sx={{ color: '#ffd93d', fontWeight: 700, mb: 1.5 }}>
                        Quote Request Process
                      </Typography>
                      {[
                        { icon: <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#55efc4' }} />, text: 'Request submitted — confirmation sent to your email' },
                        { icon: <EmailIcon sx={{ fontSize: 16, color: '#ffd93d' }} />, text: 'Our team will contact you within 1–2 business days with pricing' },
                        { icon: <ShoppingCartIcon sx={{ fontSize: 16, color: '#74b9ff' }} />, text: 'Once agreed, return here to complete your purchase' },
                      ].map((step, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {step.icon}
                          <Typography variant="caption" color="text.secondary">{step.text}</Typography>
                        </Box>
                      ))}
                      <Button fullWidth variant="outlined" size="large" disabled
                        sx={{ mt: 2, py: 1.5, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.3)', cursor: 'not-allowed' }}>
                        Checkout Unavailable — Awaiting Quote
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      fullWidth variant="contained" size="large"
                      onClick={handleCheckout}
                      disabled={cartItems.length === 0}
                      sx={{
                        mt: 4, py: 1.5, fontWeight: 600,
                        background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                        color: 'background.default',
                      }}
                    >
                      Proceed to Checkout
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Checkout Dialog */}
      <Dialog
        open={checkoutDialog}
        onClose={handleCloseCheckout}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(45, 52, 54, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '15px',
            border: '1px solid rgba(255,255,255,0.1)',
          }
        }}
      >
        <DialogTitle>
          <Stepper activeStep={activeStep} sx={{ pt: 1 }}>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent>
          {activeStep === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {paymentError && <Alert severity="error">{paymentError}</Alert>}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField name="firstName" label="First Name" value={customerInfo.firstName} onChange={handleInputChange} fullWidth required />
                </Grid>
                <Grid item xs={6}>
                  <TextField name="lastName" label="Last Name" value={customerInfo.lastName} onChange={handleInputChange} fullWidth required />
                </Grid>
              </Grid>
              <TextField name="email" label="Email" type="email" value={customerInfo.email} onChange={handleInputChange} fullWidth required />
              <TextField name="phone" label="Phone" value={customerInfo.phone} onChange={handleInputChange} fullWidth required />
              <TextField name="address" label="Shipping Address" value={customerInfo.address} onChange={handleInputChange} fullWidth multiline rows={3} required />
            </Box>
          )}

          {activeStep === 1 && clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
              <StripePaymentForm
                total={calculateTotal()}
                onSuccess={handlePaymentSuccess}
                onError={(msg) => setPaymentError(msg)}
              />
              {paymentError && <Alert severity="error" sx={{ mt: 2 }}>{paymentError}</Alert>}
            </Elements>
          )}

          {activeStep === 1 && !stripePromise && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Stripe is not configured yet. Add <strong>REACT_APP_STRIPE_PUBLISHABLE_KEY</strong> to your .env file.
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          {activeStep === 0 && (
            <>
              <Button onClick={handleCloseCheckout}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleContinueToPayment}
                disabled={!infoComplete}
                sx={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)', color: 'background.default' }}
              >
                Continue to Payment
              </Button>
            </>
          )}
          {activeStep === 1 && (
            <Button onClick={() => { setActiveStep(0); setPaymentError(''); }}>
              Back
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cart;
